import { Attr, Model, BelongsTo, HasMany } from "spraypaint";
import ApplicationRecord from "./ApplicationRecord";
import Application from "./Application";
import Employer from "./Employer";
import EmployerUser from "./EmployerUser";
import Engagement from "./Engagement";
import Placement from "./Placement";
import FeedbackTimescale from "./FeedbackTimescale";
import SubSector from "./SubSector";
import Currency from "./Currency";
import JobNotificationRecipient from "./JobNotificationRecipient";
import Feedback from "./Feedback";

@Model()
class Job extends ApplicationRecord {
  static jsonapiType = "jobs";

  @Attr() applicationsCount: number;

  @Attr() applicationStats: {
    submitted_cvs: number;
    cvs_read: number;
    candidates_at_interview: number;
    candidates_rejected: number;
    offers_made: number;
  };

  @Attr() companyId: number;

  @Attr() currencyId: number;

  @Attr() cvLimit: number;

  @Attr() degreeRequired: boolean;

  @Attr() displayState: string;

  @Attr() estimatedPlacementFee: number;

  @Attr() exclusive: boolean;

  @Attr() filledPositionsCount: number;

  @Attr() hasNewApplications: boolean;

  @Attr() hasNewEngagements: boolean;

  @Attr() interviewStages: number;

  @Attr() jobType: JobType;

  @Attr() keySkills: string;

  @Attr() keywords: string;

  @Attr() listedAt: string;

  @Attr() location: string;

  @Attr() numberOfPositions: number;

  @Attr() offerMade: boolean;

  @Attr() onHold: boolean;

  @Attr() placementFeePercent: number;

  @Attr() publishedStatus: "draft" | "live" | "under_review";

  @Attr() readApplicationsCount: number;

  @Attr() relocationAssistance: boolean;

  @Attr() salary: number;

  @Attr() salaryMin: number;

  @Attr() salaryMax: number;

  @Attr() salaryRange: string;

  @Attr() status: JobStatus;

  @Attr() title: string;

  @Attr() unreadApplicationsCount: number;

  @Attr() vacancyReason: string;

  @Attr() visaSponsorship: boolean;

  @Attr({ persist: false }) createdAt: string;

  @Attr({ persist: false }) updatedAt: string;

  @BelongsTo(Currency) currency: Currency;

  @BelongsTo(Employer) employer: Employer;

  @BelongsTo(EmployerUser) employerUser: EmployerUser;

  @HasMany(Feedback) feedbacks: Feedback[];

  @BelongsTo(FeedbackTimescale) feedbackTimescale: FeedbackTimescale;

  @BelongsTo(SubSector) sector: SubSector;

  @HasMany(Application) applications: Application[];

  @HasMany(Placement) placements: Placement[];

  @HasMany(Engagement) engagements: Engagement[];

  @HasMany(JobNotificationRecipient)
  jobNotificationRecipients: JobNotificationRecipient[];

  get competencyStageCount() {
    const actions = ["competencies_required", "competency_test_completed"];

    return this.applications.filter(({ lastAction }) =>
      actions.includes(lastAction)
    ).length;
  }

  get phoneInterviews() {
    const actions = [
      "telephone_interview_requested",
      "telephone_interview_confirmed",
    ];

    return this.applications.filter(({ lastAction }) =>
      actions.includes(lastAction)
    ).length;
  }

  get firstStageInterviews() {
    const actions = ["first_interview_requested", "first_interview_confirmed"];

    return this.applications.filter(({ lastAction }) =>
      actions.includes(lastAction)
    ).length;
  }

  get secondStageInterviews() {
    const actions = [
      "second_interview_requested",
      "second_interview_confirmed",
    ];

    return this.applications.filter(({ lastAction }) =>
      actions.includes(lastAction)
    ).length;
  }

  get candidatesRejected() {
    return this.applications.filter(
      ({ lastAction }) => lastAction === "rejected_candidate"
    ).length;
  }

  get jobHubAgentsPath() {
    return `/employer/jobs/${this.id}/applications/#agents`;
  }

  get jobHubCandidatesPath() {
    return `/employer/jobs/${this.id}/applications/#candidates`;
  }

  get jobHubJobProfilePath() {
    return `/employer/jobs/${this.id}/applications/#job-profile`;
  }
}

export enum JobType {
  PERMANENT = "permanent",
  CONTRACT = "contract",
}

export enum JobStatus {
  NEW = "new",
  OPEN = "open",
  INTERVIEW = "interview_stage",
  OFFER = "offer_made",
  EXPIRED = "expired",
  FILLED = "position_filled",
  CLOSED = "closed",
}

export enum JobPublishedStatus {
  DRAFT = "0",
  REVIEW = "1",
  LIVE = "2",
}

export default Job;
